import { useEffect, useRef, useState } from "react";
import { Editor } from "@tiptap/core";

/**
 * Mounts a Tiptap editor onto a DOM node and returns the ref to attach.
 *
 * This stands in for @tiptap/react's useEditor, which we cannot use: every
 * @tiptap/react release from 2.5.0 on is built on useSyncExternalStore and
 * declares a React >=17 peer dependency, and this app is on React 16.13.
 * @tiptap/core itself has no React dependency at all, so the only thing
 * missing is the ~40 lines below. When React is upgraded, delete this file
 * and swap in useEditor - nothing else in the component has to change.
 */
export default function useTiptapEditor({
  extensions,
  content = "",
  editable = true,
  onUpdate,
  onCreate,
}) {
  const hostRef = useRef(null);
  const editorRef = useRef(null);
  const [editor, setEditor] = useState(null);

  // The HTML this editor has emitted or applied, most recent first. The sync
  // effect below uses it to tell an external change from an echo of our own;
  // without it, every keystroke would re-run setContent and reset the caret.
  //
  // A history rather than just the latest value: React can flush the [content]
  // effect from an earlier commit after a newer keystroke has already updated
  // the editor, so a late echo carries HTML we emitted keystrokes ago.
  const emittedRef = useRef([content]);
  const remember = (html) => {
    const seen = emittedRef.current;
    if (seen[0] === html) return;
    seen.unshift(html);
    if (seen.length > 10) seen.length = 10;
  };

  // Held in refs so a caller passing inline arrows does not remount the editor
  // on every render.
  const onUpdateRef = useRef(onUpdate);
  const onCreateRef = useRef(onCreate);
  onUpdateRef.current = onUpdate;
  onCreateRef.current = onCreate;

  // Mount once. `extensions` define the ProseMirror schema and are read here
  // only - to change them, remount the component with a different `key`.
  useEffect(() => {
    if (!hostRef.current) return undefined;

    const instance = new Editor({
      element: hostRef.current,
      extensions,
      content,
      editable,
      // where Tiptap's focus command additionally calls
      // `view.dom.focus()` synchronously (isiOS/isAndroid, and Chrome's device
      // emulation spoofs the user agent) and perturbs the view in the middle of
      // the command, after its transaction was built.
      onCreate: ({ editor: ed }) => {
        if (onCreateRef.current) onCreateRef.current(ed);
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML();
        remember(html);
        if (onUpdateRef.current) onUpdateRef.current(html, ed);
      },
    });

    editorRef.current = instance;
    // Read back rather than trusting `content`: Tiptap normalises on parse, so
    // the HTML it holds is rarely byte-identical to what was passed in.
    remember(instance.getHTML());
    setEditor(instance);

    return () => {
      instance.destroy();
      editorRef.current = null;
      setEditor(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply content changed by the parent (a different notice loaded, a draft
  // restored) without destroying the user's cursor.
  useEffect(() => {
    const ed = editorRef.current;
    if (!ed || content == null) return;
    // Anything this editor produced is an echo, however late it arrives.
    if (emittedRef.current.indexOf(content) !== -1) return;

    // A genuine external change. setContent replaces the whole document and
    // leaves the selection at the end, so put the caret back where it was.
    const { from, to } = ed.state.selection;
    // Tiptap 3 takes an options object here; Tiptap 2 took positional args.
    ed.commands.setContent(content, { emitUpdate: false });
    remember(ed.getHTML());
    const max = ed.state.doc.content.size;
    try {
      ed.commands.setTextSelection({
        from: Math.min(from, max),
        to: Math.min(to, max),
      });
    } catch (err) {
      // The old offsets may not resolve in the new document.
    }
  }, [content]);

  useEffect(() => {
    if (editorRef.current) editorRef.current.setEditable(editable);
  }, [editable]);

  return { hostRef, editor };
}
